import { create } from 'zustand';
import type { WalletTransaction } from './walletStore';
import { onSignOut } from '../lib/signOutReset';

/**
 * The coin credits waiting to be announced, and the one being announced now.
 *
 * ⚠️ DELIBERATELY NOT PERSISTED and not synced, for the same reason as
 * `capacityBlockStore`: this is state about a moment. A queue restored on
 * reload would fly coins across the screen for money that arrived yesterday.
 *
 * ⭐ The unit is a LEDGER ROW, never a balance delta. Sign up on the 1st of the
 * month and the welcome bundle and the monthly allowance are both claimed
 * inside the SAME `get_my_wallet()` call — a balance watcher sees one jump of
 * 350cp and can name no reason for it, while the ledger has two rows that each
 * know what they are. The row id is also the dedupe key, which a delta has no
 * equivalent of at all.
 */
export interface CoinGrant {
  /** The `wallet_transactions` row id — the dedupe key. */
  id: string;
  kind: WalletTransaction['kind'];
  /** Always positive: only credits are announced. */
  amountCp: number;
  note: string;
  createdAt: string;
}

/**
 * How many row ids to remember. The list exists to stop a re-read of the same
 * 50 rows announcing them twice, so it only has to outlive the read window —
 * 200 is four times the page size and costs nothing.
 */
const ANNOUNCED_CAP = 200;

interface CoinRewardState {
  queue: CoinGrant[];
  current: CoinGrant | null;
  /** Row ids already announced (or deliberately suppressed). Newest first. */
  announced: string[];
  /**
   * Which user's history has been suppressed.
   *
   * ⚠️⚠️ KEYED BY USER, NOT A BOOLEAN. Sign out and in as someone else and a
   * plain "seeded" flag would carry over — the new account's first read would
   * be treated as a later one, and their entire ledger would be announced in a
   * row. Two accounts on one machine is exactly the dogfooding setup.
   */
  seededFor: string | null;
  /**
   * A credit arrived and the mobile drawer has not been opened since.
   *
   * On a phone the wallet is not on screen at all, so the coin flies to the
   * menu button; this is what it leaves behind there.
   */
  pendingDot: boolean;
  /** First read for this user: remember these ids WITHOUT announcing them. */
  seed: (userId: string, suppressIds: string[]) => void;
  /** Add credits that have not been seen before, oldest first. */
  enqueue: (grants: CoinGrant[]) => void;
  /** Move the head of the queue into `current`, if nothing is showing. */
  advance: () => void;
  /** The current announcement is over. */
  finish: () => void;
  clearDot: () => void;
  reset: () => void;
}

const cap = (ids: string[]) => (ids.length > ANNOUNCED_CAP ? ids.slice(0, ANNOUNCED_CAP) : ids);

export const useCoinRewardStore = create<CoinRewardState>()((set, get) => ({
  queue: [],
  current: null,
  announced: [],
  seededFor: null,
  pendingDot: false,

  seed: (userId, suppressIds) =>
    set((s) => ({ seededFor: userId, announced: cap([...suppressIds, ...s.announced]) })),

  enqueue: (grants) => {
    const { announced, queue, current } = get();
    const seen = new Set([...announced, ...queue.map((g) => g.id), ...(current ? [current.id] : [])]);
    const fresh = grants.filter((g) => g.amountCp > 0 && !seen.has(g.id));
    if (fresh.length === 0) return;
    set({
      queue: [...queue, ...fresh],
      // ⚠️ Marked at ENQUEUE, not at show. Two refreshes can land inside one
      // announcement's 2.9 seconds, and the second would otherwise queue the
      // same rows again behind the first.
      announced: cap([...fresh.map((g) => g.id), ...announced]),
      pendingDot: true,
    });
  },

  advance: () =>
    set((s) => (s.current || s.queue.length === 0 ? s : { current: s.queue[0], queue: s.queue.slice(1) })),

  finish: () => set({ current: null }),

  clearDot: () => set({ pendingDot: false }),

  reset: () => set({ queue: [], current: null, announced: [], seededFor: null, pendingDot: false }),
}));

/* Forgotten on sign-out (lib/signOutReset): a credit still queued or flying, and the dot it
   left on the menu, belong to the account that left. Local only — nothing here is synced. */
onSignOut(() => useCoinRewardStore.getState().reset());
